import {
  WSActions,
  WSAlert,
  WSCard,
  WSList
} from "@wingspanhq/fe-component-library";
import { WSCardHeaderProps } from "@wingspanhq/fe-component-library/dist/lib/components/WSCard/WSCardHeader";
import { useState } from "react";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { selectorMethodExistsInData } from "../../selectors/selectorMethodExistsInData";
import { List } from "./List";
import { MethodsData, MethodValue } from "./types";

export type ManageMethodsProps = {
  data: MethodsData;

  initialValue: MethodValue | undefined;
  header?: WSCardHeaderProps;
  onAddNew?: () => void;
  addNewLabel?: string;
  onSubmit: (value: MethodValue | null) => void;
  isLoading?: boolean;
  error?: any;
};

export const ManageMethods: React.FC<ManageMethodsProps> = props => {
  // The configured method (e.g. payrollSettings.fundingSource) can point at
  // an account that's since been filtered out of the visible list (numbers
  // gone stale, canBeUsedFor.payments flipped false). Treat that as "no
  // selection yet" rather than silently keeping the hidden id as the value,
  // so Save can't resubmit it unchanged and the payer is prompted to pick
  // one of the accounts actually shown (PLA-3248).
  const [initialValueMissing] = useState(
    () =>
      !!props.initialValue &&
      !selectorMethodExistsInData({
        data: props.data,
        value: props.initialValue
      })
  );

  const [value, setValue] = useState<MethodValue | null | undefined>(
    initialValueMissing ? undefined : props.initialValue
  );

  return (
    <WSList gap="2XL">
      {initialValueMissing && (
        <WSAlert theme="warning" icon="alert" title="Selection needed">
          Your current method needs attention and isn't available to select.
          Choose one of the methods below to continue.
        </WSAlert>
      )}

      <WSCard header={props.header}>
        <WSList gap="L">
          <List
            data={props.data}
            initialValue={props.initialValue}
            value={value}
            onChange={setValue}
          />

          {props.onAddNew && (
            <WSCard
              header={{
                label: {
                  link: {
                    icon: "plus-circle",
                    onClick: props.onAddNew,
                    label: props.addNewLabel
                  }
                }
              }}
            />
          )}
        </WSList>
      </WSCard>

      <WSErrorMessage contextKey="FundingMethod" error={props.error} />

      <WSActions
        alignment="fill"
        buttons={[
          {
            disabled: value === undefined,
            label: "Save changes",
            onClick: () => {
              if (value !== undefined) {
                props.onSubmit(value);
              }
            },
            loading: props.isLoading
          }
        ]}
      />
    </WSList>
  );
};
